import React from "react";
import {Link} from "react-router-dom";
import {
    IconButton,
    makeStyles,
    Typography
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableContainer
    from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import {Edit, Add, Delete} from "@material-ui/icons";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import {DragDropContext, Droppable, Draggable} from "react-beautiful-dnd";
import {toast} from "react-toastify";
import {
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useTable,
} from "react-table";
import apiHelper from "../../../util/ApiHelper";
import {CustomCheckbox} from "../../../util";
import {useLoading} from "../../../util/LoadingProvider";
import {
    PaginationHandler
} from "../../../util/useManualPagination";
import PaginationBarForHandler from "../../common/PaginationBarForHandler";

const useStyles = makeStyles((theme) => ({
    content: {
        "& .ck-editor__editable_inline": {
            minHeight: "200px",
        },
    },
    text: {
        textDecoration: "none",
        color: "#306F9D",
    },
    pagination: {
        "& .MuiTextField-root": {
            width: "35px",
            minWidth: "auto",
        },
        "& .MuiSelect-root": {
            paddingTop: "3px",
            paddingBottom: "3px",
        },
        "& input": {
            padding: "4px 0",
            marginLeft: "10px",
            marginRight: "10px",
        },
        "& button": {
            background: "white",
            color: "#cccccc",
            border: "1px solid #CCCCCC",
            minWidth: "auto",
            width: "24px",
            height: "24px",
            margin: "4px 0",
            "&.left": {
                borderRadius: "4px 0 0 4px",
                marginLeft: "10px",
            },
            "&.right": {
                borderRadius: " 0 4px 4px 0",
            },
        },
    },
}));

const IndeterminateCheckbox = React.forwardRef(
    ({indeterminate, ...rest}, ref) => {
        const defaultRef = React.useRef();
        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return <CustomCheckbox type="checkbox"
                               ref={resolvedRef} {...rest} />;
    }
);

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const PartnerBenefitListPage = (props) => {
    const {match, disabled} = props;
    const classes = useStyles();
    const [keyword, setKeyword] = React.useState("");
    const [data, setData] = React.useState([]);
    const [totalPages, setTotalPages] = React.useState(1);
    const [originalOrder, setOriginalOrder] = React.useState([]);
    const [hasOrderChanged, setHasOrderChanged] = React.useState(false);

    const {setLoading} = useLoading();

    // for pagination
    const [paginationHandler] = React.useState(new PaginationHandler());
    const [elementCounts, setElementCounts] = React.useState({
        all: 0,
    });

    const CustomLink = React.useMemo(
        () =>
            React.forwardRef((linkProps, ref) => <Link
                ref={ref} {...linkProps} />),
        []
    );


    const columns = React.useMemo(
        () => [
            {
                Header: "ID",
                accessor: "id",
                Cell: ({row}) => {
                    return (
                        <span className={classes.text}>
                            {row.original.id}
                        </span>
                    );
                },
            },
            {
                Header: "Code",
                accessor: "code",
                Cell: ({ row }) => {
                    return <span>{row.original.code || "-"}</span>;
                },
            },
            {
                Header: "Logo",
                accessor: "logoPath",
                disableGlobalFilter: true,
                Cell: ({ row }) => {
                    const logoUrl = row.original.logoPath;
                    if (!logoUrl) {
                        return <span>-</span>;
                    }
                    return (
                        <a
                            href={logoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: "inline-block" }}
                        >
                            <img
                                src={logoUrl}
                                alt="Partner Logo"
                                style={{
                                    width: "60px",
                                    height: "60px",
                                    objectFit: "cover",
                                    borderRadius: "4px",
                                    cursor: "pointer"
                                }}
                            />
                        </a>
                    );
                },
            },
            {
                Header: "URL",
                accessor: "url",
                Cell: ({ row }) => {
                    const url = row.original.url;
                    if (!url) {
                        return <span>-</span>;
                    }
                    return (
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#306F9D", textDecoration: "none" }}
                        >
                            {url.length > 50 ? `${url.substring(0, 50)}...` : url}
                        </a>
                    );
                },
            },
            {
                Header: "Status",
                accessor: "status",
                Cell: ({ row }) => {
                    return <span>{row.original.status || "-"}</span>;
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [classes.text]
    );

    const {
        getTableProps,
        headerGroups,
        rows,
        prepareRow,
        setPageSize,
        gotoPage,
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
        state: {pageIndex, pageSize},
    } = useTable(
        {
            columns,
            data,
            initialState: {pageIndex: 0, pageSize: 10},
            manualPagination: true,
            pageCount: totalPages,
        },
        useGlobalFilter,
        usePagination,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                {
                    id: "selection",
                    Header: ({getToggleAllRowsSelectedProps}) => (
                        <div>
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        </div>
                    ),
                    Cell: ({row}) => (
                        <div>
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                },
                ...columns,
                {
                    id: "action",
                    Header: () => <div>Action</div>,
                    Cell: ({row}) => (
                        <div
                            style={{display: "inline-flex", gap: "8px"}}>
                            <IconButton
                                size="small"
                                component={CustomLink}
                                to={`${match.url}/${row.original.id}/edit`}
                                title="Edit"
                            >
                                <Edit fontSize="small"/>
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => handleDeletePartner(row.original.id, row.original.code)}
                                title="Delete"
                                style={{ color: '#f44336' }}
                            >
                                <Delete fontSize="small"/>
                            </IconButton>
                        </div>
                    ),
                },
                {
                    id: "dragHandle",
                    Header: () => <></>,
                },
            ]);
        }
    );

    const getPartners = React.useCallback(async (searchKeyword = null) => {
        try {
            setLoading(true);
            const result = await apiHelper.cineclub.getPartners(
                searchKeyword || null,
                paginationHandler.pageIndex,
                paginationHandler.pageSize * paginationHandler.pageIndex,
                paginationHandler.pageSize,
                false
            );
            console.log('PARTNER RESULT:', result)  
            
            var output = result.data.content.map(function(x){
                x.id = x.id.toString();
                return x;
            });
            
            paginationHandler.setPageIndex(0).setRows(output);
            const all = result.data.totalElements;
            const totalPages = result.data.totalPages;

            setElementCounts({
                all
            });
            const rowsData = paginationHandler.setPageIndex(0).getRows();
            setData(rowsData);
            // Store original order for comparison
            setOriginalOrder(rowsData.map(item => item.id));
            setHasOrderChanged(false);
            console.log(paginationHandler.pageSize);
            setTotalPages(Math.ceil(result.data.totalElements/paginationHandler.pageSize));
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [paginationHandler, setLoading]);

    React.useEffect(() => {
        if (paginationHandler) {
            getPartners(); // only get partners if paginationHandler ready
        }
    }, [getPartners, paginationHandler]);

    const triggerSearch = React.useCallback(() => {
        console.log("KEYWORD: "+keyword);
        getPartners(keyword || null);
        gotoPage(0);
    }, [gotoPage, keyword, getPartners]);

    const handleDeletePartner = React.useCallback(async (partnerId, partnerCode) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete the partner "${partnerCode || partnerId}"? This action cannot be undone.`
        );
        
        if (!confirmed) {
            return;
        }

        try {
            setLoading(true);
            const response = await apiHelper.cineclub.deletePartner(partnerId);
            
            if (response.data && response.data.code === 0) {
                // Success - refresh the partner list
                await getPartners();
                alert("Partner deleted successfully!");
            } else {
                console.error("Failed to delete partner:", response);
                alert("Failed to delete partner. Please try again.");
            }
        } catch (error) {
            console.error("Error deleting partner:", error);
            alert("An error occurred while deleting the partner. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [getPartners, setLoading]);

    const onDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        const items = reorder(data, result.source.index, result.destination.index);
        setData(items);
        
        // Check if order has changed
        const newOrder = items.map(item => item.id);
        const orderChanged = JSON.stringify(newOrder) !== JSON.stringify(originalOrder);
        setHasOrderChanged(orderChanged);
    };

    const handleSaveOrdering = React.useCallback(async () => {
        try {
            setLoading(true);
            // Format: { id: sequence }
            const seqData = {};
            data.forEach((item, index) => {
                seqData[item.id] = index + 1;
            });
            
            const response = await apiHelper.cineclub.bulkUpdatePartnerSequence(seqData);
            
            if (response.data && response.data.code === 0) {
                toast.dark("Your changes have been saved.");
                // Refresh the partner list to get updated sequences
                await getPartners();
                setHasOrderChanged(false);
            } else {
                console.error("Failed to save ordering:", response);
                alert("Failed to save ordering. Please try again.");
            }
        } catch (error) {
            console.error("Error saving ordering:", error);
            alert("An error occurred while saving ordering. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [data, getPartners, setLoading]);

    return (
        <div className={classes.content}>
            <Typography variant="h4" gutterBottom>
                {"Cineclub > Partner Benefit List"}
            </Typography>
            <form>
                <Grid
                    container
                    justify="space-between"
                    style={{paddingBottom: "10px"}}
                >
                    <Grid container item xs={6} md={6}>
                        {/* Bulk actions section can be added here if needed */}
                    </Grid>
                    <Grid container item xs={6} md={6}>
                        <Box
                            width="100%"
                            component="span"
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                        >
                            <Box display="flex">
                                <TextField
                                    id="outlined-error"
                                    label="Search"
                                    variant="outlined"
                                    margin="dense"
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                />
                            </Box>
                            {hasOrderChanged && (
                                <Box display="flex" style={{ marginRight: "10px" }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        type="button"
                                        onClick={handleSaveOrdering}
                                    >
                                        Save Changes
                                    </Button>
                                </Box>
                            )}
                            <Box display="flex">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    type="button"
                                    margin="dense"
                                    onClick={triggerSearch}
                                >
                                    Search
                                </Button>
                            </Box>
                            <Box display="flex" style={{ marginRight: "10px" }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="button"
                                    component={Link}
                                    to={`${match.url}/create`}
                                    startIcon={<Add />}
                                >
                                    Create Partner
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                <Grid container xs={12}>
                    <TableContainer
                        component={Paper}
                        style={{
                            boxShadow: "none",
                            border: "1px solid #cccccc",
                        }}
                    >
                        <fieldset disabled={disabled}
                                  className="fieldset">
                            <Table
                                aria-label="simple table"
                                component="ul"
                                style={{margin: "0"}}
                                {...getTableProps()}
                            >
                                <colgroup>
                                    <col
                                        style={{width: "1%"}}/>
                                    <col
                                        style={{width: "5%"}}/>
                                    <col
                                        style={{width: "10%"}}/>
                                    <col
                                        style={{width: "10%"}}/>
                                    <col
                                        style={{width: "30%"}}/>
                                    <col
                                        style={{width: "10%"}}/>
                                    <col
                                        style={{width: "5%"}} />
                                    <col
                                        style={{width: "3%"}} />
                                </colgroup>
                                <TableHead component="div">
                                    {headerGroups.map((headerGroup) => (
                                        <TableRow {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map((column) => (
                                                <TableCell
                                                    {...column.getHeaderProps()}
                                                    className={`${column.className ?? ""}`}
                                                    style={{
                                                        paddingTop: 6,
                                                        paddingBottom: 6,
                                                        lineHeight: "1.1em",
                                                        height: "48px",
                                                    }}
                                                >
                                                    {column.render("Header")}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHead>
                                <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId="droppable" type="PARTNERS">
                                        {(provided, snapshot) => (
                                            <TableBody
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                            >
                                                {rows.map((row, index) => {
                                                    prepareRow(row);
                                                    return (
                                                        <Draggable
                                                            key={row.id}
                                                            draggableId={String(row.id)}
                                                            index={index}
                                                            isDragDisabled={disabled}
                                                        >
                                                            {(provided, snapshot) => (
                                                                <TableRow
                                                                    {...row.getRowProps()}
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    style={{
                                                                        ...provided.draggableProps.style,
                                                                        backgroundColor: snapshot.isDragging ? '#f5f5f5' : 'inherit',
                                                                    }}
                                                                >
                                                                    {row.cells.map((cell) => {
                                                                        if (cell.column.id === "dragHandle") {
                                                                            return (
                                                                                <TableCell
                                                                                    key={cell.column.id}
                                                                                    style={{
                                                                                        paddingTop: 6,
                                                                                        paddingBottom: 6,
                                                                                        lineHeight: "1.1em",
                                                                                        height: "48px",
                                                                                        color: "#CCCCCC",
                                                                                    }}
                                                                                    {...provided.dragHandleProps}
                                                                                >
                                                                                    <DragHandleIcon />
                                                                                </TableCell>
                                                                            );
                                                                        }
                                                                        return (
                                                                            <TableCell
                                                                                {...cell.getCellProps()}
                                                                                className={`${cell.column.className ?? ""}`}
                                                                                style={{
                                                                                    paddingTop: 6,
                                                                                    paddingBottom: 6,
                                                                                    lineHeight: "1.1em",
                                                                                    height: "48px",
                                                                                }}
                                                                            >
                                                                                {cell.render("Cell")}
                                                                            </TableCell>
                                                                        );
                                                                    })}
                                                                </TableRow>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}
                                                {provided.placeholder}
                                            </TableBody>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            </Table>
                        </fieldset>
                    </TableContainer>
                </Grid>
                <Grid
                    container
                    justify="flex-end"
                    style={{
                        paddingRight: "25px",
                        marginTop: "15px"
                    }}
                >
                    <div>
                        <div></div>
                        <PaginationBarForHandler
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            setData={setData}
                            setTotalPages={setTotalPages}
                            paginationHandler={paginationHandler}
                            gotoPage={gotoPage}
                            pageIndex={pageIndex}
                            canPreviousPage={canPreviousPage}
                            previousPage={previousPage}
                            nextPage={nextPage}
                            canNextPage={canNextPage}
                            elementCounts={elementCounts}
                            status={null}
                        />
                    </div>
                </Grid>
            </form>
        </div>
    );
};

export default PartnerBenefitListPage;

